import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { partnerSettingsKeys } from "@/components/pages/partner-integration-settings/hooks/partner-settings.hooks";
import { getPartnerAnalyticsPeriodParams } from "@/components/pages/guesteria-partners/utils/partnerAnalyticsPeriod";
import { useHotel } from "@/hooks/useHotel";
import { partnerSettingsService } from "@/services/partner-settings-service";
import type { PartnerRevenueAnalyticsPeriod } from "@/types/partner-revenue-analytics";
import type { PartnerProviderType } from "@/types/partner-settings";
import { is404 } from "@/utils/httpErrors";

export const usePartnerOrdersSummary = (
  period: PartnerRevenueAnalyticsPeriod,
  provider: PartnerProviderType | undefined,
  enabled = true,
) => {
  const { hotelId } = useHotel();
  const periodParams = useMemo(
    () => getPartnerAnalyticsPeriodParams(period),
    [period],
  );

  return useQuery({
    queryKey: partnerSettingsKeys.ordersSummary(
      hotelId || "",
      period,
      provider || "",
    ),
    enabled: Boolean(hotelId) && Boolean(provider) && enabled,
    queryFn: async () => {
      try {
        return await partnerSettingsService.getPartnerOrdersSummary(hotelId!, {
          from: periodParams.from,
          to: periodParams.to,
          provider,
        });
      } catch (e) {
        if (is404(e)) {
          return null;
        }
        throw e;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });
};
